import { Resources } from 'constants/resources';

import { useNumberBooleanChoices } from 'hooks/useNumberBooleanChoices';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { useGetList, useGetMany } from 'react-admin';
import { Controller, useFormContext } from 'react-hook-form';
import { DateTimeRangeTimezonePicker } from 'shared/mui/DatePicker/DateTimeRangeTimezonePicker/DateTimeRangeTimezonePicker';
import { FilterFieldConfig } from 'shared/mui/Filter/config';
import { useFilterContext } from 'shared/mui/Filter/context/filterContext';
import { useFilterTimezone } from 'shared/mui/Filter/hooks/useFilterTimezone';
import {
  AutocompleteMultiselectInput,
  EnumAutocompleteInput,
  SelectInput,
  TextInput,
} from 'shared/react-admin/Inputs';
import { ReferenceArrayInput } from 'shared/react-admin/ReferenceInputs';

export const useOrderProviderExternalErrorsFilterFields = (): FilterFieldConfig[] => {
  const translate = useTypedTranslate();
  const { control, resetField, setValue, watch, getValues } = useFormContext();
  const { timezone, onChangeTimezone } = useFilterTimezone();
  const { setAppliedFilter, appliedFilters, onReset } = useFilterContext();
  const numberBooleanChoices = useNumberBooleanChoices();
  const { data: categories } = useGetList('external_error_categories');

  const { merchant, provider } = watch();

  const { data: gateways } = useGetList(
    Resources.Gateways,
    {
      filter: {
        merchantAccounts: { merchants: merchant },
        provider,
      },
    },
    { enabled: !!merchant.length && !!provider.length },
  );

  const { data: defaultCategories } = useGetMany('external_error_categories', {
    ids: getValues('providerExternalError.category'),
  });

  return [
    {
      name: 'appliedDate',
      label: translate('app.filter.creationTime'),
      chipType: 'dateTimeRangeTimezone',
      chipProps: {
        onDelete: onReset,
      },
      input: (
        <Controller
          control={control}
          name="appliedDate"
          render={({ field: { onChange, onBlur, value }, formState: { errors }, fieldState }) => (
            <DateTimeRangeTimezonePicker
              error={!!errors.appliedDate}
              helperText={fieldState?.error?.message}
              label={translate('app.filter.creationTimePeriod')}
              maxDate={new Date()}
              onBlur={onBlur}
              onChange={onChange}
              onChangeTimezone={onChangeTimezone}
              required
              sx={{ marginBottom: 0 }}
              timezone={timezone}
              value={value}
            />
          )}
        />
      ),
    },
    {
      name: 'providerExternalError.category',
      label: translate('app.filter.categories'),
      chipType: 'multiselect',
      chipProps: {
        resource: 'external_error_categories',
        optionText: 'description',
        options: defaultCategories || [],
      },
      input: (
        <AutocompleteMultiselectInput
          filterToQuery={(searchText: string) => ({ name: searchText })}
          helperText={false}
          label={translate('app.filter.categories')}
          name="providerExternalError.category"
          optionText="description"
          options={categories || []}
        />
      ),
    },
    {
      name: 'providerExternalError.reason',
      label: translate('app.filter.declineReason'),
      chipType: 'text',
      input: (
        <TextInput
          fullWidth
          helperText={false}
          label={translate('app.filter.declineReason')}
          resettable
          source="providerExternalError.reason"
          variant="outlined"
        />
      ),
    },
    {
      name: 'providerExternalError.isCritical',
      label: translate('app.filter.critical'),
      chipType: 'text',
      chipProps: {
        getName: (filterValue: string) =>
          numberBooleanChoices.find((value) => value.id === filterValue)?.label || '',
      },
      input: (
        <SelectInput
          choices={numberBooleanChoices}
          fullWidth
          helperText={false}
          label={translate('app.filter.critical')}
          name="providerExternalError.isCritical"
          optionText="label"
          source="providerExternalError.isCritical"
          variant="outlined"
        />
      ),
    },
    {
      name: 'merchant',
      label: translate('app.filter.merchants'),
      chipType: 'multiselect',
      chipProps: {
        resource: 'merchants',
        limitTags: 1,
        onApply: () => {
          resetField('provider', { keepDirty: true });
          resetField('method', { keepDirty: true });
          resetField('order.gateway', { keepDirty: true });

          setAppliedFilter('provider', []);
          setAppliedFilter('method', []);
          setAppliedFilter('order.gateway', []);
        },
        onDelete: () => {
          resetField('provider', { keepDirty: true });
          resetField('method', { keepDirty: true });
          resetField('order.gateway', { keepDirty: true });

          setAppliedFilter('provider', null);
          setAppliedFilter('method', []);
          setAppliedFilter('order.gateway', []);
        },
        onReset: () => {
          setValue('provider', appliedFilters?.provider);
          setValue('method', appliedFilters?.method);
          setValue('order.gateway', appliedFilters?.gateway);

          setAppliedFilter('provider', appliedFilters?.provider);
          setAppliedFilter('method', appliedFilters?.method);
          setAppliedFilter('order.gateway', appliedFilters?.gateway);
        },
      },
      input: (variant) => (
        <ReferenceArrayInput perPage={100} reference="merchants" source="merchant">
          <AutocompleteMultiselectInput
            filterToQuery={(searchText: string) => ({ name: searchText })}
            helperText={false}
            label={translate('app.filter.merchants')}
            name="merchant"
            {...(variant === 'field' && {
              onChange: () => {
                setValue('provider', []);
                setValue('method', []);
                setValue('order.gateway', []);
              },
            })}
            optionText="name"
          />
        </ReferenceArrayInput>
      ),
    },
    {
      name: 'provider',
      label: translate('app.filter.provider'),
      chipType: 'multiselect',
      chipProps: {
        resource: 'providers',
        limitTags: 1,
        onApply: () => {
          resetField('method', { keepDirty: true });
          resetField('order.gateway', { keepDirty: true });

          setAppliedFilter('method', []);
          setAppliedFilter('order.gateway', []);
        },
        onDelete: () => {
          resetField('method', { keepDirty: true });
          resetField('order.gateway', { keepDirty: true });

          setAppliedFilter('method', []);
          setAppliedFilter('order.gateway', []);
        },
        onReset: () => {
          setValue('method', appliedFilters?.method);
          setValue('order.gateway', appliedFilters?.gateway);

          setAppliedFilter('method', appliedFilters?.method);
          setAppliedFilter('order.gateway', appliedFilters?.gateway);
        },
      },
      input: (variant) => (
        <ReferenceArrayInput
          enableGetChoices={() => !!merchant.length}
          filter={{ merchantAccounts: { merchants: merchant } }}
          perPage={100}
          reference="providers"
          sort={{ field: 'name', order: 'ASC' }}
          source="provider"
        >
          <AutocompleteMultiselectInput
            {...(variant === 'field' && {
              disabled: !merchant.length,
              disabledHint: translate('app.filter.selectMerchantFirst'),
              onChange: () => {
                setValue('method', []);
                setValue('order.gateway', []);
              },
            })}
            filterToQuery={(searchText: string) => ({ name: searchText })}
            helperText={false}
            label={translate('app.filter.provider')}
            name="provider"
            optionText="name"
          />
        </ReferenceArrayInput>
      ),
    },
    {
      name: 'order.gateway',
      label: translate('app.filter.gateway'),
      chipType: 'multiselect',
      chipProps: {
        resource: 'gateways',
        limitTags: 1,
        options: gateways,
      },
      input: (variant) => (
        <AutocompleteMultiselectInput
          {...(variant === 'field' && {
            disabled: !gateways,
            disabledHint: translate('app.filter.selectProviderFirst'),
          })}
          filterToQuery={(searchText: string) => ({ name: searchText })}
          helperText={false}
          label={translate('app.filter.gateway')}
          name="order.gateway"
          optionText="name"
          options={gateways}
        />
      ),
    },
    {
      name: 'method',
      label: translate('app.filter.method'),
      chipType: 'multiselect',
      chipProps: {
        resource: 'methods',
        limitTags: 1,
      },
      input: (variant) => (
        <ReferenceArrayInput
          enableGetChoices={() => !!gateways?.length}
          filter={{ gateway: gateways?.map((gateway) => gateway?.id) }}
          perPage={100}
          reference="methods"
          source="method"
        >
          <AutocompleteMultiselectInput
            {...(variant === 'field' && {
              disabled: !gateways?.length,
              disabledHint: translate('app.filter.selectProviderFirst'),
            })}
            filterToQuery={(searchText: string) => ({ name: searchText })}
            helperText={false}
            label={translate('app.filter.method')}
            name="method"
            optionText="name"
          />
        </ReferenceArrayInput>
      ),
    },
    {
      name: 'order.direction',
      label: translate('app.filter.direction'),
      chipType: 'enumAutocomplete',
      chipProps: {
        resource: 'directions',
      },
      input: (
        <EnumAutocompleteInput
          filterToQuery={(searchText: string) => ({ description: searchText })}
          fullWidth
          helperText={false}
          label={translate('app.filter.direction')}
          name="order.direction"
          optionText="description"
          optionValue="value"
          resource="directions"
          source="order.direction"
          variant="outlined"
        />
      ),
    },
  ];
};
